import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { pagesData } from '../data/pagesContent';

export default function ContactPage() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const pageKey = isEn ? 'en-contact' : 'kontakt';
  const page = pagesData[pageKey];

  const [formData, setFormData] = useState({
    website_url: '',
    navn: '',
    telefon: '',
    email: '',
    by: '',
    besked: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(isEn ? 'Failed to send message. Please call us directly.' : 'Kunne ikke sende besked. Prøv venligst at ringe til os.');
      }

      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err.message || (isEn ? 'An error occurred. Please try again.' : 'Der opstod en fejl. Prøv venligst igen.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang={page.lang} />
        <title>{page.title}</title>
        <meta name="description" content={page.meta.description} />
        <meta property="og:title" content={page.meta.ogTitle} />
        <meta property="og:description" content={page.meta.ogDescription} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/hero.jpg" />
        <link rel="canonical" href={`https://billundvvs.netlify.app${isEn ? '/en/contact/' : '/kontakt/'}`} />
      </Helmet>

      <div className="bg-slate-50 border-b border-slate-200 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            {isEn ? 'Contact us' : 'Kontakt os'}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            {isEn
              ? 'Call, write or drop by. We answer every enquiry the same working day – and emergencies immediately. English spoken.'
              : 'Ring, skriv eller kig forbi. Vi svarer på alle henvendelser samme arbejdsdag – og ved akutte skader med det samme.'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Company Details Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 sm:p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Billund Varme & Sanitet A/S
              </h2>

              <div className="space-y-4 text-sm text-slate-700">
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-[#d3102e] w-4" aria-hidden="true"></i>
                  <a href="tel:+4575331276" className="font-bold hover:text-[#d3102e] transition-colors">
                    {isEn ? '+45 75 33 12 76' : '75 33 12 76'}
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-[#d3102e] w-4" aria-hidden="true"></i>
                  <a href="mailto:info@billundvvs.dk" className="hover:text-[#d3102e] transition-colors">
                    info@billundvvs.dk
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-location-dot text-[#d3102e] w-4 mt-1" aria-hidden="true"></i>
                  <div>
                    <p>Montanavej 1B</p>
                    <p>{isEn ? 'DK-7190 Billund' : '7190 Billund'}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {isEn ? 'VAT no. DK29407320' : 'CVR 29407320'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2">
                  {isEn ? 'Opening hours' : 'Åbningstider'}
                </h3>
                <div className="space-y-1 text-xs text-slate-600">
                  <p>{isEn ? 'Monday–Thursday 7:00–16:00' : 'Mandag–torsdag 7.00–16.00'}</p>
                  <p>{isEn ? 'Friday 7:00–13:00' : 'Fredag 7.00–13.00'}</p>
                  <p className="font-semibold text-slate-900 mt-2">
                    {isEn ? 'Emergency service every day – call +45 75 33 12 76' : 'Døgnvagt alle dage – ring 75 33 12 76'}
                  </p>
                </div>
              </div>
            </div>

            {/* Map info placeholder */}
            <div className="bg-slate-100 border border-slate-200 rounded-xl p-5 text-center text-xs text-slate-600">
              <i className="fa-solid fa-map-location-dot text-2xl text-slate-400 mb-2 block"></i>
              <span>{isEn ? 'Map: Montanavej 1B, 7190 Billund' : 'Kort: Montanavej 1B, 7190 Billund'}</span>
            </div>
          </div>

          {/* Contact Form Column */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-xs">
              <h2 className="text-2xl font-extrabold text-slate-900 mb-6">
                {isEn ? 'Write to us' : 'Skriv til os'}
              </h2>

              {submitSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center my-4">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">
                    {isEn ? 'Message sent!' : 'Besked sendt!'}
                  </h3>
                  <p className="text-sm text-green-800">
                    {isEn
                      ? 'Thank you for reaching out. We will get back to you as soon as possible.'
                      : 'Tak for din henvendelse. Vi svarer dig hurtigst muligt.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Antispam honeypot */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
                    <label htmlFor="website_url">Don’t fill this out:</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website_url}
                      onChange={handleChange}
                    />
                  </div>

                  {submitError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-xs sm:text-sm text-red-700">
                      {submitError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        {isEn ? 'Name' : 'Navn'} *
                      </label>
                      <input
                        type="text"
                        required
                        name="navn"
                        value={formData.navn}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        {isEn ? 'Phone' : 'Telefon'} *
                      </label>
                      <input
                        type="tel"
                        required
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        {isEn ? 'Email' : 'E-mail'} *
                      </label>
                      <input
                        type="email"
                        required
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                        {isEn ? 'Postcode and town' : 'Postnummer og by'}
                      </label>
                      <input
                        type="text"
                        name="by"
                        value={formData.by}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      {isEn ? 'Message' : 'Besked'} *
                    </label>
                    <textarea
                      required
                      rows={5}
                      name="besked"
                      value={formData.besked}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                    ></textarea>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#d3102e] hover:bg-[#a50d26] disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider px-7 py-3 rounded-md transition-colors inline-flex items-center gap-2"
                    >
                      <span>{isSubmitting ? (isEn ? 'Sending...' : 'Sender...') : (isEn ? 'Send message →' : 'Send besked →')}</span>
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 pt-2">
                    {isEn ? (
                      <>
                        By sending you accept our{' '}
                        <Link to="/en/privacy-policy" className="text-[#d3102e] hover:underline">
                          privacy policy
                        </Link>
                        . We only use your details to answer your enquiry.
                      </>
                    ) : (
                      <>
                        Ved at sende accepterer du vores{' '}
                        <Link to="/privatlivspolitik" className="text-[#d3102e] hover:underline">
                          privatlivspolitik
                        </Link>
                        . Vi bruger kun dine oplysninger til at besvare din henvendelse.
                      </>
                    )}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
